import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../api'
import { useAsync } from '../lib/useAsync'
import { useToast } from '../components/Toasts'
import { ErrorState, LoadingBlock } from '../components/States'
import { BackButton } from '../components/BackButton'
import { recordName } from '../lib/unify'

/**
 * One market record, and editing it.
 *
 * **Titled with the job, because the job is what was opened.** It used to
 * carry the survey's name, so every one of eight hundred records in a file
 * was headed "Mercer 2026" and the row somebody had clicked was the second
 * box down the page. The survey, the year and the row number belong
 * underneath: they say where this row came from, which is context rather
 * than identity.
 *
 * Every value is a plain text box. The module does not know what a column
 * means, so it cannot judge whether a number is reasonable, and it will not
 * pretend to: whatever she types is what is stored.
 *
 * **There is no add-a-record form anywhere and there is not going to be one.**
 * Every row here came out of a provider's file, which is what lets the numbers
 * be trusted. One hand-typed row among eight hundred licensed ones, with
 * nothing marking it as different, quietly costs that.
 */
export function MarketRecordScreen() {
  const { id = '' } = useParams()
  const nav = useNavigate()
  const toast = useToast()
  const loaded = useAsync(() => api.getMarketRecord(id), [id])

  const [edits, setEdits] = useState<Record<string, string> | null>(null)
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)

  if (loaded.loading) return <LoadingBlock label="Loading record" />
  if (loaded.error) return <ErrorState message={loaded.error} onRetry={loaded.reload} />
  if (!loaded.data) return <ErrorState message="That record no longer exists." />

  const { record, survey } = loaded.data
  const values = edits ?? record.values
  /*
   * The job, then its code, and only then the survey. A record from a file
   * carrying neither has no name of its own, and saying the survey is more
   * honest than inventing one.
   */
  const named = recordName(record.values, survey.columns)
  const from = `${survey.provider} ${survey.year}`.trim()
  const dirty = edits !== null && survey.columns.some((c) => (edits[c.key] ?? '') !== (record.values[c.key] ?? ''))

  async function save() {
    if (!edits) return
    setBusy(true)
    try {
      const changed: Record<string, string> = {}
      for (const col of survey.columns) {
        if ((edits[col.key] ?? '') !== (record.values[col.key] ?? '')) {
          changed[col.key] = edits[col.key] ?? ''
        }
      }
      const next = await api.updateMarketRecord(record.id, changed)
      loaded.set({ record: next, survey })
      setEdits(null)
      toast.ok('Saved.')
    } catch (err) {
      toast.fail((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    setBusy(true)
    try {
      const { surveyRemoved } = await api.deleteMarketRecord(record.id)
      toast.ok(surveyRemoved ? 'Record deleted. That was the survey’s last row, so the survey has gone too.' : 'Record deleted.')
      nav('/market-data')
    } catch (err) {
      toast.fail((err as Error).message)
      setBusy(false)
    }
  }

  return (
    <section className="screen screen--narrow">
      <BackButton to="market data" onClick={() => nav('/market-data')} />

      <header className="screen__head">
        <h1 className="page">{named ?? from}</h1>
        {/*
          Where it came from, not what it is called. The file name is not
          here: it is on the Surveys screen, which is where somebody checking
          which file a figure came from is already going.
        */}
        <p className="subline">
          {named ? `${from} · row ${record.sourceLine}` : `Row ${record.sourceLine}`}
        </p>
      </header>

      <div className="card card--pad stack">
        {survey.columns.map((col) => (
          <label className="field" key={col.key}>
            {/* The provider's own heading. Not ours, not tidied up. */}
            <span>
              {col.heading}
              {/*
                Only where the heading does not already say it. Radford writes
                "Base Salary P50 USD", and appending "(USD)" to that is the
                same word twice in a label that is already long. The table has
                always guarded this; this screen did not.
              */}
              {col.currency && !col.heading.toUpperCase().includes(col.currency.toUpperCase()) && (
                <span className="subline"> ({col.currency})</span>
              )}
            </span>
            <input
              value={values[col.key] ?? ''}
              onChange={(e) =>
                setEdits({ ...(edits ?? record.values), [col.key]: e.target.value })
              }
            />
          </label>
        ))}

        {/*
          Who last changed it and when. Not decoration: a number that changed
          with nobody's name against it is worse than a number that was wrong.
        */}
        {record.updatedBy && (
          <p className="hint">
            Last changed by {record.updatedBy}
            {record.updatedAt ? ` on ${new Date(record.updatedAt).toLocaleDateString()}` : ''}.
          </p>
        )}

        <div className="form-foot">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void save()}
            disabled={!dirty || busy}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="btn btn--danger"
            onClick={() => setConfirming(true)}
            disabled={busy}
          >
            Delete this record
          </button>
        </div>
      </div>

      {confirming && (
        <div className="card card--pad confirm" role="alertdialog" aria-labelledby="del-rec">
          <h2 id="del-rec" className="confirm__title">Delete this record?</h2>
          <p className="confirm__body">
            It came from {survey.provider} {survey.year}. There is no archive and nothing comes
            back.
          </p>
          <div className="actions">
            <button type="button" className="btn btn--danger-solid" onClick={() => void remove()}>
              Delete permanently
            </button>
            <button type="button" className="btn" onClick={() => setConfirming(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
